import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Globe } from "lucide-react";
import { useAdminDomains } from "@/hooks/useAdminDomainQueries";
import { formatDate as formatDateShared } from "@/lib/utils";
import {
  DOMAIN_STATUSES,
  type DomainStatus,
  type DomainRegistrationAdminDto,
} from "@/types/domain";

/**
 * Maps a domain status to a shadcn Badge variant.
 * Failure/terminal states are destructive; Active is default; others secondary.
 */
function statusVariant(
  status: DomainStatus,
): "default" | "secondary" | "destructive" | "outline" | "warning" {
  switch (status) {
    case "Active":
      return "default";
    case "PendingRegistration":
    case "Registering":
    case "TransferPending":
    case "Expiring":
      return "warning";
    case "RegistrationFailed":
    case "TransferFailed":
      return "destructive";
    case "Expired":
    case "RedemptionPeriod":
      return "destructive";
    case "Suspended":
      return "outline";
    case "Cancelled":
      return "secondary";
    default:
      return "secondary";
  }
}

/** Formats an ISO date string for display, or "—" when null. */
function formatDate(value: string | null): string {
  if (!value) return "—";
  return formatDateShared(value);
}

export function DomainOperationsPage() {
  const [status, setStatus] = useState<DomainStatus | "All">("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detailDomain, setDetailDomain] =
    useState<DomainRegistrationAdminDto | null>(null);

  const filters = { status, page, pageSize };
  const { data, isLoading, error, refetch } = useAdminDomains(filters);

  const domains = data?.data ?? [];
  const pagination = data?.pagination;
  const totalCount = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.currentPage ?? 1;

  const startItem = domains.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(
    currentPage * (pagination?.itemsPerPage ?? pageSize),
    totalCount,
  );

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Domain Operations"
          subtitle="Domain registrations across all tenants"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">
                Failed to load domain registrations
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domain Operations"
        subtitle="Read-only view of domain registrations across all tenants"
      />

      {/* Summary Card */}
      <div className="grid gap-4 md:grid-cols-1">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Domain Registrations
            </CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
            <p className="text-xs text-muted-foreground">
              {status === "All" ? "All statuses" : `Filtered by ${status}`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Filter domain registrations by status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value as DomainStatus | "All");
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                {DOMAIN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Data Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Domain Registrations ({totalCount})</CardTitle>
              <CardDescription>
                {totalCount > 0
                  ? `Showing ${startItem}-${endItem} of ${totalCount} registrations`
                  : "No domain registrations found"}
              </CardDescription>
            </div>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => {
                setPageSize(parseInt(value, 10));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                Loading domain registrations...
              </p>
            </div>
          ) : domains.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">
                No domain registrations found
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {status !== "All"
                  ? "Try adjusting the status filter"
                  : "No domains have been registered yet"}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Domain</TableHead>
                    <TableHead scope="col">Status</TableHead>
                    <TableHead scope="col">Tenant</TableHead>
                    <TableHead scope="col">Registrar</TableHead>
                    <TableHead scope="col">Years</TableHead>
                    <TableHead scope="col">Auto-Renew</TableHead>
                    <TableHead scope="col">Registered</TableHead>
                    <TableHead scope="col">Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domains.map((domain) => (
                    <TableRow key={domain.id}>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          className="text-primary hover:underline cursor-pointer"
                          onClick={() => setDetailDomain(domain)}
                        >
                          {domain.fqdn}
                        </button>
                        {domain.failureReason && (
                          <p
                            className="mt-0.5 text-xs text-destructive-ink truncate max-w-[200px]"
                            title={domain.failureReason}
                          >
                            {domain.failureReason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(domain.status)}>
                          {domain.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{domain.tenantName || "—"}</TableCell>
                      <TableCell>{domain.registrarProvider}</TableCell>
                      <TableCell>{domain.registrationYears}</TableCell>
                      <TableCell>{domain.autoRenew ? "Yes" : "No"}</TableCell>
                      <TableCell>{formatDate(domain.registeredAt)}</TableCell>
                      <TableCell>{formatDate(domain.expiresAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Domain Detail Dialog */}
      <Dialog
        open={!!detailDomain}
        onOpenChange={(open) => !open && setDetailDomain(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailDomain?.fqdn}</DialogTitle>
          </DialogHeader>
          <DialogBody withoutFooter>
            {detailDomain && (
              <dl className="grid gap-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd>
                    <Badge variant={statusVariant(detailDomain.status)}>
                      {detailDomain.status}
                    </Badge>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Tenant</dt>
                  <dd>{detailDomain.tenantName}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Registrar</dt>
                  <dd>{detailDomain.registrarProvider}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Registration Term</dt>
                  <dd>{detailDomain.registrationYears} year(s)</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Auto-Renew</dt>
                  <dd>{detailDomain.autoRenew ? "Yes" : "No"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Registered</dt>
                  <dd>{formatDate(detailDomain.registeredAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Expires</dt>
                  <dd>{formatDate(detailDomain.expiresAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>{formatDate(detailDomain.createdAt)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Order Item ID</dt>
                  <dd className="font-mono text-xs">
                    {detailDomain.orderItemId ?? "—"}
                  </dd>
                </div>
                {detailDomain.failureReason && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Failure Reason</dt>
                    <dd
                      className="text-destructive-ink text-right max-w-[250px] truncate"
                      title={detailDomain.failureReason}
                    >
                      {detailDomain.failureReason}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Registration ID</dt>
                  <dd className="font-mono text-xs">{detailDomain.id}</dd>
                </div>
              </dl>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}
